import * as Sentry from "@sentry/react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { PersistentDrawer } from "@/components/Drawer";
import { HotelDetailHeader } from "@/features/hotels/components/hotel/HotelDetailHeader";
import { HotelDetailsStickySearchDrawer } from "@/features/hotels/components/hotel/HotelDetailsStickySearchDrawer";
import { HotelOverview } from "@/features/hotels/components/hotel/HotelOverview";
import { PaymentOptions } from "@/features/hotels/components/PaymentOptions";
import { useHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { getTotalVisitors } from "@/features/hotels/utils/rooms";
import { HotelTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import { getDateString } from "@/utils/dates";

export const Route = createFileRoute(
  "/_authenticated/hotels/_hotels/_hotel-offer/$id/details",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

/**
 * First step of the hotel booking process.
 */
function RouteComponent() {
  const intl = useIntl();
  const navigate = Route.useNavigate();

  const { data: hotel } = useHotelDataContext();
  const { searchData } = useHotelSearchDataContext();

  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);

  if (!hotel) {
    // If the app fails to load the hotel data or to pass search data, redirect to the hotel list page
    // This is a fallback in case the user tries to access this page directly
    // It's unlikely that this will happen, but it's a good fallback to have
    // Avoids the need to handle these cases in the other components
    Sentry.captureMessage(
      "User tried to access hotel details page directly without going through the hotel offer page",
      "fatal",
    );
    return <Navigate to="/hotels/list" />;
  }

  const originalTotalPrice = hotel.minPrice?.value || 0;

  const handleSearchDrawerOpen = () => {
    setSearchDrawerOpen(true);

    if (!searchData) return;

    rudderAnalytics.track(
      HotelTrackingEvents.USER_CLICKED_EDIT_HOTEL_DETAILS_PAGE,
      {
        hotel_name: hotel.name,
        current_start_date: getDateString(searchData.dates.checkIn),
        current_end_date: getDateString(searchData.dates.checkOut),
        current_travellers_count: getTotalVisitors(searchData.rooms),
        current_rooms_count: searchData.rooms.length,
      },
    );
  };

  return (
    <>
      <div className="mb-2 px-main">
        <HotelDetailHeader
          hotel={hotel}
          onSearchDrawerOpen={handleSearchDrawerOpen}
        />
      </div>
      <div className="px-main">
        <HotelOverview hotel={hotel} />
      </div>
      <PersistentDrawer>
        <PaymentOptions totalPrice={originalTotalPrice} />

        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            navigate({ to: "/hotels/$id/rooms" });
          }}
        >
          {intl.formatMessage({
            defaultMessage: "Select a room",
            description:
              "Label for the button that takes the user to the next step of the hotel booking process, where they can select a room",
          })}
        </Button>
      </PersistentDrawer>

      <HotelDetailsStickySearchDrawer
        open={searchDrawerOpen}
        onClose={() => setSearchDrawerOpen(false)}
      />
    </>
  );
}
